import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthService, Kindergarten } from '@isophi/core-legacy';
import { ToastService } from '@isophi/core-ui';
import { ChildGroupSuggestion } from '@isophi/mng-api';
import { TranslocoService } from '@jsverse/transloco';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

import { WorkplaceApiService } from '../../services/workplace-api.service';

@Component({
  selector: 'app-transfer-children-modal',
  templateUrl: './transfer-children-modal.component.html',
  styleUrls: ['./transfer-children-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TransferChildrenModalComponent implements OnInit, OnDestroy {
  @Input() selectedChildrenCount = 0;
  @Input() currentWorkplaceUuid: string | null = null;

  workplaces: Kindergarten[] = [];
  classes$: Observable<ChildGroupSuggestion[]> = of([]);
  isLoadingClasses = false;

  form: FormGroup;

  private readonly subscription = new Subscription();

  constructor(
    public activeModal: NgbActiveModal,
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private workplaceApiService: WorkplaceApiService,
    private toastr: ToastService,
    private translocoService: TranslocoService,
    private cdr: ChangeDetectorRef
  ) {
    this.form = this.formBuilder.group({
      workplaceUuid: [null, Validators.required],
      classUuid: [{ value: null, disabled: true }],
    });
  }

  ngOnInit(): void {
    const teacher = this.authService.loggedUser?.teacher;
    this.workplaces = teacher?.kindergartens || [];

    this.subscription.add(
      this.form.controls.workplaceUuid.valueChanges
        .pipe(
          tap((workplaceUuid: string | null) => {
            this.form.controls.classUuid.setValue(null);
            this.isLoadingClasses = !!workplaceUuid;
            this.cdr.markForCheck();
            if (workplaceUuid) {
              this.form.controls.classUuid.enable({ emitEvent: false });
            } else {
              this.form.controls.classUuid.disable({ emitEvent: false });
            }
          }),
          switchMap((workplaceUuid: string | null) => {
            if (!workplaceUuid) {
              return of([] as ChildGroupSuggestion[]).pipe(
                tap(() => {
                  this.isLoadingClasses = false;
                  this.cdr.markForCheck();
                })
              );
            }
            return this.workplaceApiService.getClassesSuggestions(workplaceUuid).pipe(
              catchError(() => {
                this.toastr.error(this.translocoService.translate('transfer/loadClassesFailed'));
                return of([] as ChildGroupSuggestion[]);
              }),
              tap(() => {
                this.isLoadingClasses = false;
                this.cdr.markForCheck();
              })
            );
          })
        )
        .subscribe((classes) => {
          this.classes$ = of(classes || []);
          this.cdr.markForCheck();
        })
    );
  }

  get workplaceControl(): FormControl {
    return this.form.get('workplaceUuid') as FormControl;
  }

  get classControl(): FormControl {
    return this.form.get('classUuid') as FormControl;
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.activeModal.close({
      workplaceUuid: this.form.value.workplaceUuid,
      classUuid: this.form.value.classUuid ?? null,
    });
  }

  cancel(): void {
    this.activeModal.dismiss();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
